import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useArtifactHistoryStore } from "../FileViewer/artifactHistoryStore";

/**
 * How a viewer presents its content (#9746).
 *
 * `overlay` is a drawer over the page; `split` is a resizable half-and-half
 * pane. They are not the same content at two sizes — only overlay has room for
 * the details panel (document info, path, AI summary, extracted metadata), so
 * the choice is about whether the left pane is worth its half of the screen.
 */
export type ViewerMode = "overlay" | "split";

/**
 * Surfaces that host a viewer, grouped by what sits in the left pane. That
 * grouping is what decides the default, so the mode is stored per group.
 *
 * - `context`: chat, reports, matrix, agents. The left pane holds the answer
 *   whose citation was clicked, i.e. the thing the document is being checked
 *   against, so it has to stay on screen.
 * - `documents` / `rms`: the left pane is a list, which is dead weight once a
 *   document is open, and the details panel is what the reader wants next.
 */
export type ViewerSurface = "rms" | "documents" | "context";

/**
 * `documents` is the one overlay surface: you picked the file from a list that
 * has nothing more to say once it is open, and the details panel is what you
 * want beside it instead.
 *
 * Everywhere else defaults to side-by-side, because the left pane is holding
 * something you are reading the document *against* — the answer that cited it,
 * or the RMS record it belongs to. RMS looked like an overlay surface right up
 * until #9746 found out why: a permanently-mounted answer dialog was
 * intercepting every document open and forcing the drawer. That was the bug
 * this issue was filed about, not a preference, and KSBE has asked for
 * side-by-side there twice.
 */
export const DEFAULT_VIEWER_MODE: Record<ViewerSurface, ViewerMode> = {
  rms: "split",
  documents: "overlay",
  context: "split",
};

export const VIEWER_MODE_CHANGE_EVENT = "viewerModeChange";

const isMode = (value: string | null): value is ViewerMode =>
  value === "overlay" || value === "split";

// Matches the whole first path segment, so /rmsomething and /notetaker/rms
// are not mistaken for the RMS product.
const firstSegment = (pathname: string): string =>
  pathname.replace(/^\/+/, "").split("/")[0] ?? "";

export function viewerSurfaceForPath(pathname: string): ViewerSurface {
  const segment = firstSegment(pathname);
  if (segment === "rms") return "rms";
  if (segment === "documents") return "documents";
  return "context";
}

/**
 * Whether the route draws its own document viewer beside its content, so the
 * shell's split pane must stay shut for every open on it (#12091). An agent
 * conversation — inside a run or on the legacy session route — renders a
 * split-pane `<FileViewer />` off the shared viewer state; the shell mounting
 * its own as well shows the document twice. The overlay drawer still follows
 * the viewer mode. A run with no conversation open draws no viewer, so it is
 * not one of these.
 */
/**
 * The frame width from which a route that hosts its own viewer can show its
 * document beside its content, two columns at their floors. The page reads
 * it for its layout; the shell reads it to leave the page that much when it
 * keeps an artifact pane open alongside (#12091). Shared here so neither
 * imports the other.
 */
export const HOSTED_VIEWER_TWO_COLUMN_PX = 720;

export function routeHostsOwnViewer(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "agents") return false;
  return (
    (segments.length === 6 &&
      segments[2] === "runs" &&
      segments[4] === "conversations") ||
    (segments.length === 4 && segments[2] === "sessions")
  );
}

/**
 * Whether the route presents every viewer as the overlay, whatever the reader
 * chose elsewhere (dashboard-assistant-polish-4 requirement 30, #13602): a
 * dashboard page keeps its width and its editor (the grid and the assistant's
 * rail) under a document or artifact, so a side split never takes half of it.
 */
export function routePresentsOverlayOnly(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  return (
    segments.length >= 3 &&
    segments[0] === "rms" &&
    segments[1] === "dashboards"
  );
}

export function viewerModeStorageKey(surface: ViewerSurface): string {
  return `fp-viewer-mode:${surface}`;
}

export function getViewerMode(pathname: string): ViewerMode {
  if (routePresentsOverlayOnly(pathname)) return "overlay";
  const surface = viewerSurfaceForPath(pathname);
  try {
    const stored = localStorage.getItem(viewerModeStorageKey(surface));
    if (isMode(stored)) {
      return stored;
    }
  } catch {
    // Private-mode / disabled storage: fall through to the default.
  }
  return DEFAULT_VIEWER_MODE[surface];
}

export function setViewerMode(pathname: string, mode: ViewerMode): void {
  const surface = viewerSurfaceForPath(pathname);
  try {
    localStorage.setItem(viewerModeStorageKey(surface), mode);
  } catch {
    // Storage is best-effort; the event below still re-presents the open
    // viewer for this session.
  }
  window.dispatchEvent(
    new CustomEvent(VIEWER_MODE_CHANGE_EVENT, { detail: { surface, mode } })
  );
}

/**
 * Current mode for the route the caller is rendered on, plus a setter. Re-reads
 * on route change and on any other component's write, so an open viewer
 * re-presents without needing to be reopened.
 */
export function useViewerMode(): [ViewerMode, (mode: ViewerMode) => void] {
  const { pathname } = useLocation();
  const [mode, setMode] = useState<ViewerMode>(() => getViewerMode(pathname));

  useEffect(() => {
    setMode(getViewerMode(pathname));
    const sync = () => setMode(getViewerMode(pathname));
    window.addEventListener(VIEWER_MODE_CHANGE_EVENT, sync);
    // Another tab writing the same key should not leave this one disagreeing.
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(VIEWER_MODE_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [pathname]);

  const set = useCallback(
    (next: ViewerMode) => setViewerMode(pathname, next),
    [pathname]
  );

  return [mode, set];
}

// ── Artifact viewer mode ──
//
// Artifacts carry their OWN mode, separate from the per-surface document
// choice, defaulting to side-by-side everywhere (Gerry, 2026-09-01): an
// artifact opens beside what produced or cites it, whatever the surface's
// document preference says, and the overlay is the opt-in. The one exception
// is a dashboard page (`routePresentsOverlayOnly`, #13602), where an artifact
// always opens as the overlay.

const ARTIFACT_MODE_KEY = "fp-viewer-mode:artifact";

// A transient presentation override (#11819): version history shows the
// artifact in the overlay for as long as it is open, without touching the
// remembered preference. It lives in the artifact-history store (#12187) —
// page-scoped state with an owner the story harness resets — and deliberately
// not in storage, since it must not survive a reload and clearing it is what
// hands the pane back.
export function overrideArtifactViewerMode(mode: ViewerMode | null): void {
  if (useArtifactHistoryStore.getState().artifactModeOverride === mode) return;
  useArtifactHistoryStore.setState({ artifactModeOverride: mode });
  window.dispatchEvent(
    new CustomEvent(VIEWER_MODE_CHANGE_EVENT, {
      detail: { surface: "artifact", mode: getArtifactViewerMode() },
    })
  );
}

export function getArtifactViewerMode(): ViewerMode {
  // Ahead of every override: even a chat hand-off's side-by-side open
  // presents as the overlay on such a route.
  if (routePresentsOverlayOnly(window.location.pathname)) return "overlay";
  const { artifactModeOverride } = useArtifactHistoryStore.getState();
  if (artifactModeOverride) return artifactModeOverride;
  try {
    const stored = localStorage.getItem(ARTIFACT_MODE_KEY);
    if (isMode(stored)) {
      return stored;
    }
  } catch {
    // Private-mode / disabled storage: fall through to the default.
  }
  return "split";
}

/** Chat handed an artifact to a conversation (#13261): it opens side by
 *  side with that conversation for this open, whatever presentation the
 *  reader last chose, without touching that choice. The override ends with
 *  the next artifact open (Layout's open funnel) or the reader's own choice
 *  of mode. */
export function presentArtifactBesideChat(): void {
  if (getArtifactViewerMode() === "overlay") {
    overrideArtifactViewerMode("split");
  }
}

/** A new chat a hand-off opened its artifact on became its conversation
 *  (/chat -> /chat/<id> on the first send): the open is still that chat's,
 *  so the mark follows it. */
export function followChatHandoff(from: string, to: string): void {
  const { chatHandoff } = useArtifactHistoryStore.getState();
  if (chatHandoff !== null && chatHandoff.pathname === from) {
    useArtifactHistoryStore.setState({
      chatHandoff: { ...chatHandoff, pathname: to },
    });
  }
}

/** Ends `presentArtifactBesideChat`'s override, if it is in force. */
export function endArtifactBesideChat(): void {
  if (useArtifactHistoryStore.getState().artifactModeOverride === "split") {
    overrideArtifactViewerMode(null);
  }
}

export function setArtifactViewerMode(mode: ViewerMode): void {
  // The reader's own choice ends a chat hand-off's side-by-side open.
  if (useArtifactHistoryStore.getState().artifactModeOverride === "split") {
    useArtifactHistoryStore.setState({ artifactModeOverride: null });
  }
  try {
    localStorage.setItem(ARTIFACT_MODE_KEY, mode);
  } catch {
    // Storage is best-effort; the event below still re-presents the open
    // viewer for this session.
  }
  window.dispatchEvent(
    new CustomEvent(VIEWER_MODE_CHANGE_EVENT, {
      detail: { surface: "artifact", mode },
    })
  );
}

/** Artifact-mode counterpart of useViewerMode: one remembered choice for
 *  every route, read at mount and on every mode change, except on a route
 *  that presents only the overlay (a dashboard page), where it reads
 *  `overlay`. */
export function useArtifactViewerMode(): [
  ViewerMode,
  (mode: ViewerMode) => void,
] {
  const [mode, setMode] = useState<ViewerMode>(getArtifactViewerMode);

  useEffect(() => {
    const sync = () => setMode(getArtifactViewerMode());
    window.addEventListener(VIEWER_MODE_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(VIEWER_MODE_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return [mode, setArtifactViewerMode];
}

/** Layout → drawer artifact-pane opens; the drawer → its teardown mirrors
 *  through "closeArtifactOverlay". Constants so a typo is a type error. */
export const OPEN_ARTIFACT_OVERLAY_EVENT = "openArtifactOverlay";
export const CLOSE_ARTIFACT_OVERLAY_EVENT = "closeArtifactOverlay";
